import React, { useEffect, useState } from "react";
import { Menu } from "@headlessui/react";
import toast from "react-hot-toast";
import { HiDotsVertical } from "react-icons/hi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import api from "@/services/api";
import useStore from "@/services/store";
import DebounceInput from "@/components/DebounceInput";
import MultiSelect from "@/components/MultiSelect";

import DealDrawer from "./dealDrawer";
import FollowUp from "./followUp";
import NewDeal from "./newDeal";
import Stats from "./stats";
import {
  BLOCKERS,
  SALES_STAGES,
  actionReasons,
  blockerColor,
  blockerLabel,
  byHeat,
  byNeedsAction,
  dealAmount,
  formatCurrency,
  formatDate,
  isOverdue,
  isProbablyDead,
  needsActionNow,
  stageColor,
  stageLabel,
  stageStep,
  urgencySplit,
  visibleUrgencyBand,
} from "./constants";

const EMPTY_FILTERS = {
  search: "",
  needs_action: false,
  person_id: "",
  sales_stage: [],
  blocker: "",
};

export default function Board() {
  // Kanban and table are two ways of looking at the same filtered list, so the view is separate
  // from the filters — switching view never changes which deals you are looking at.
  const [view, setView] = useState("kanban");
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [deals, setDeals] = useState([]);
  const [people, setPeople] = useState([]);
  const [budgetsByProject, setBudgetsByProject] = useState({});
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [selected, setSelected] = useState(null);

  const { businessUnit } = useSelector((state) => state.Auth);
  const organisation = useStore((state) => state.organisation);

  const fetchDeals = async () => {
    try {
      const query = { stage: ["prospection"], status: "active", per_page: 200, page: 1, sort: "next_action_at" };
      if (filters.search) query.search = filters.search;
      if (filters.person_id) query.owner_id = filters.person_id;
      if (filters.needs_action) query.needs_action = true;
      if (filters.sales_stage.length) query.sales_stage = filters.sales_stage;
      if (filters.blocker) query.blocker = filters.blocker;
      if (businessUnit._id !== "ALL") query.business_unit_id = businessUnit._id;
      if (organisation && organisation._id !== "ALL") query.organisation_id = organisation._id;

      const { ok, data } = await api.post("/project/search", query);
      if (!ok) throw new Error("Failed to fetch deals");
      setDeals(data.projects);

      const projectIds = data.projects.map((p) => p._id);
      if (!projectIds.length) return setBudgetsByProject({});

      const budgets = await api.post("/budget/search", { projectIds, status: "active", visibility: "public" });
      if (!budgets.ok) throw new Error("Failed to fetch budgets");
      const grouped = {};
      for (const b of budgets.data) {
        if (!grouped[b.projectId]) grouped[b.projectId] = [];
        grouped[b.projectId].push(b);
      }
      setBudgetsByProject(grouped);
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch deals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, [filters, organisation, businessUnit._id]);

  // The people row is built from the deals on screen, and frozen while a person is selected: filtering
  // on someone would otherwise hide everyone else and leave no square to switch to.
  useEffect(() => {
    if (filters.person_id) return;
    const byId = {};
    for (const d of deals) {
      for (const role of ["sales_owner", "closing_owner", "next_action_owner"]) {
        const id = d[`${role}_id`];
        if (id) byId[id] = { _id: id, name: d[`${role}_name`], avatar: d[`${role}_avatar`] };
      }
    }
    setPeople(Object.values(byId).sort((a, b) => (a.name || "").localeCompare(b.name || "")));
  }, [deals]);

  const update = async (id, payload) => {
    try {
      const { ok } = await api.put(`/project/${id}/sales`, payload);
      if (!ok) return toast.error("Failed to update deal");
      await fetchDeals();
    } catch (error) {
      console.error(error);
      toast.error("Failed to update deal");
    }
  };

  // Rescores exactly the deals on screen, so the button is cheap and its effect is visible.
  const rescore = async () => {
    if (!window.confirm(`Ask Jev to score ${deals.length} deals ?`)) return;
    setScoring(true);
    try {
      const { ok, code, data } = await api.post("/project/sales-score", { ids: deals.map((d) => d._id) });
      if (!ok) return toast.error(code === "TYPESAFE_API_KEY_MISSING" ? "Jev is not configured on the API" : "Failed to score deals");
      toast.success(`${data.scored} deals scored${data.failed ? `, ${data.failed} failed` : ""}`);
      await fetchDeals();
    } catch (error) {
      console.error(error);
      toast.error("Failed to score deals");
    } finally {
      setScoring(false);
    }
  };

  // Clicking the selected person again clears the filter, from the squares or from the stats.
  const selectPerson = (id) => setFilters((f) => ({ ...f, person_id: f.person_id === id ? "" : id }));

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center p-0.5 rounded-md bg-gray-100">
          <Tab active={view === "kanban"} onClick={() => setView("kanban")}>
            Kanban
          </Tab>
          <Tab active={view === "table"} onClick={() => setView("table")}>
            Table
          </Tab>
          <Tab active={view === "stats"} onClick={() => setView("stats")}>
            Stats
          </Tab>
        </div>
        <div className="flex items-center gap-2">
          <NewDeal
            onCreated={async (deal) => {
              await fetchDeals();
              setSelected(deal);
            }}
          />
          <BoardMenu
            filters={filters}
            businessUnit={businessUnit}
            organisation={organisation}
            onRescore={rescore}
            canRescore={!scoring && deals.length > 0}
            scoring={scoring}
          />
        </div>
      </div>

      <People people={people} selectedId={filters.person_id} onSelect={selectPerson} />

      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-56">
          <DebounceInput
            debounce={300}
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            className="input w-full"
            placeholder="Search a deal"
          />
        </div>
        <Toggle
          active={filters.needs_action}
          onClick={() => setFilters((f) => ({ ...f, needs_action: !f.needs_action }))}
          title="Overdue, missing an owner or a dated next action, or blocked. Jev's own call on whether it needs a person this week sorts the list — nothing is hidden.">
          Needs action now
        </Toggle>
        {view === "table" && (
          <div className="w-48">
            <MultiSelect
              id="select-sales-stage"
              options={SALES_STAGES.map((s) => ({ value: s.value, label: s.label }))}
              values={filters.sales_stage.map((value) => ({ value, label: stageLabel(value) }))}
              onSelectedChange={(e) => setFilters((f) => ({ ...f, sales_stage: e.map((o) => o.value) }))}
              placeholder="Stage"
            />
          </div>
        )}
        <select className="input w-48" value={filters.blocker} onChange={(e) => setFilters({ ...filters, blocker: e.target.value })}>
          <option value="">All blockers</option>
          {BLOCKERS.map((b) => (
            <option key={b.value} value={b.value}>
              {b.label}
            </option>
          ))}
        </select>
        <button type="button" onClick={() => setFilters(EMPTY_FILTERS)} className="text-sm text-gray-500 hover:text-gray-800 underline">
          Reset
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : !deals.length ? (
        <EmptyState needsAction={filters.needs_action} />
      ) : view === "stats" ? (
        <Stats deals={deals} budgetsByProject={budgetsByProject} selectedPersonId={filters.person_id} onSelectPerson={selectPerson} />
      ) : view === "kanban" ? (
        <Kanban deals={deals} budgetsByProject={budgetsByProject} onUpdate={update} onOpen={setSelected} />
      ) : (
        <DealTable deals={[...deals].sort(byHeat)} budgetsByProject={budgetsByProject} onOpen={setSelected} />
      )}

      <DealDrawer
        deal={selected ? deals.find((d) => d._id === selected._id) : null}
        budgets={selected ? budgetsByProject[selected._id] || [] : []}
        onClose={() => setSelected(null)}
        onUpdate={update}
      />
    </div>
  );
}

const Tab = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-3 py-1.5 text-sm font-medium rounded ${active ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-800"}`}>
    {children}
  </button>
);

// "Get context" copies a prompt that lets Claude list, add and update the deals of this board through the
// accounting MCP connector, scoped to the business unit and the filters on screen.
const BoardMenu = ({ filters, businessUnit, organisation, onRescore, canRescore, scoring }) => {
  const copyContext = async () => {
    const hasBusinessUnit = businessUnit._id !== "ALL";
    const scope = hasBusinessUnit ? `business unit "${businessUnit.name}" (id ${businessUnit._id})` : "the business unit I name (ask me which one)";
    const businessUnitId = hasBusinessUnit ? businessUnit._id : "<business unit id>";

    const search = { businessUnitId, stage: "prospection", status: "active" };
    if (organisation && organisation._id !== "ALL") search.organisationId = organisation._id;
    if (filters.search) search.search = filters.search;
    if (filters.person_id) search.ownerId = filters.person_id;
    if (filters.needs_action) search.needsAction = true;
    if (filters.sales_stage.length) search.salesStage = filters.sales_stage;
    if (filters.blocker) search.blocker = filters.blocker;

    const text = [
      `Work on the Sales → Signature deals of ${scope} via the accounting MCP connector.`,
      `A deal is a project at stage "prospection". Always pass the business unit id.`,
      `list   : search_projects ${JSON.stringify(search)}`,
      `read   : get_project {"id": "<deal id>"}`,
      `add    : create_project {"name": "<company — opportunity>", "stage": "prospection", "businessUnitId": "${businessUnitId}"}`,
      `update : update_project {"id": "<deal id>", "fields": {"sales_stage", "next_action", "next_action_at", "blocker", "description", "comments", ...}}`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(text);
      toast.success("Context copied");
    } catch (e) {
      toast.error("Failed to copy");
    }
  };

  return (
    <Menu as="div" className="relative">
      <Menu.Button className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100" title="More">
        <HiDotsVertical className="h-5 w-5" />
      </Menu.Button>
      <Menu.Items className="absolute right-0 z-20 mt-1 w-48 rounded-md bg-white py-1 shadow-lg border border-gray-200 focus:outline-none">
        <Menu.Item>
          {({ active }) => (
            <button type="button" onClick={copyContext} className={`w-full px-3 py-1.5 text-left text-sm text-gray-700 ${active ? "bg-gray-100" : ""}`}>
              Get context
            </button>
          )}
        </Menu.Item>
        <Menu.Item disabled={!canRescore}>
          {({ active }) => (
            <button
              type="button"
              onClick={onRescore}
              disabled={!canRescore}
              className={`w-full px-3 py-1.5 text-left text-sm text-gray-700 disabled:opacity-50 ${active ? "bg-gray-100" : ""}`}>
              {scoring ? "Scoring…" : "Rescore with Jev"}
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <Link to="/sales/snapshot" className={`block px-3 py-1.5 text-sm text-gray-700 ${active ? "bg-gray-100" : ""}`}>
              HubSpot snapshot
            </Link>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
};

// One square per person on the deals. Clicking one keeps the deals where that person is the sales owner,
// the closing owner or owns the next action; clicking it again clears the filter.
const People = ({ people, selectedId, onSelect }) => {
  if (!people.length) return null;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {people.map((p) => (
        <button
          key={p._id}
          type="button"
          title={p.name}
          onClick={() => onSelect(p._id)}
          className={`w-10 h-10 rounded-md overflow-hidden ring-2 transition ${
            selectedId === p._id ? "ring-blue-600" : selectedId ? "ring-transparent opacity-40 hover:opacity-100" : "ring-transparent hover:ring-gray-300"
          }`}>
          {p.avatar ? (
            <img src={p.avatar} alt={p.name} className="w-full h-full object-cover" />
          ) : (
            <span className="w-full h-full flex items-center justify-center bg-gray-100 text-xs font-semibold text-gray-600">
              {(p.name || "?")
                .split(" ")
                .map((w) => w[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()}
            </span>
          )}
        </button>
      ))}
    </div>
  );
};

const Toggle = ({ active, onClick, title, children }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`px-3 py-1.5 text-sm font-medium border rounded-md whitespace-nowrap ${
      active ? "bg-blue-600 border-blue-600 text-white hover:bg-blue-700" : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
    }`}>
    {children}
  </button>
);

const EmptyState = ({ needsAction }) => (
  <div className="bg-white rounded-lg border border-gray-200 py-12 text-center">
    <h3 className="text-lg font-medium text-gray-900">{needsAction ? "Nothing needs action" : "No deals match"}</h3>
    <p className="mt-1 text-sm text-gray-500">
      {needsAction ? "Every deal has an owner, a dated next action and no blocker." : "Add one with New deal, or clear the filters if you are expecting to see some."}
    </p>
  </div>
);

// ─── Kanban ──────────────────────────────────────────────────────────────────
// Stage changes by dragging. Relancé / Done live on the card so a follow-up already done can
// quiet the red Act without opening the deal. Everything else is edited in the drawer.

const Kanban = ({ deals, budgetsByProject, onUpdate, onOpen }) => {
  const [draggedId, setDraggedId] = useState(null);
  const [overStage, setOverStage] = useState(null);

  const drop = (stage) => {
    setOverStage(null);
    const deal = deals.find((d) => d._id === draggedId);
    setDraggedId(null);
    if (!deal || (deal.sales_stage || "new") === stage) return;
    onUpdate(deal._id, { sales_stage: stage });
  };

  return (
    <div className="flex gap-3 overflow-x-auto pb-4 items-start">
      {SALES_STAGES.map((stage) => {
        const stageDeals = deals.filter((d) => (d.sales_stage || "new") === stage.value).sort(byNeedsAction);
        const total = stageDeals.reduce((sum, d) => sum + (dealAmount(d, budgetsByProject[d._id]).amount || 0), 0);

        return (
          <div
            key={stage.value}
            onDragOver={(e) => {
              e.preventDefault();
              setOverStage(stage.value);
            }}
            onDragLeave={() => setOverStage((s) => (s === stage.value ? null : s))}
            onDrop={() => drop(stage.value)}
            className={`flex flex-col w-72 shrink-0 rounded-lg border ${overStage === stage.value ? "border-blue-400 bg-blue-50" : "border-gray-200 bg-gray-50"}`}>
            <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-b ${stageColor(stage.value)}`}>
              <span className="text-xs font-semibold uppercase tracking-wide">{stage.label}</span>
              <span className="text-xs font-medium">
                {stageDeals.length} • {formatCurrency(total)}
              </span>
            </div>
            <div className="p-2 space-y-2 min-h-[80px]">
              {stageDeals.map((deal) => (
                <KanbanCard
                  key={deal._id}
                  deal={deal}
                  budgets={budgetsByProject[deal._id] || []}
                  dragging={draggedId === deal._id}
                  onDragStart={() => setDraggedId(deal._id)}
                  onDragEnd={() => setDraggedId(null)}
                  onOpen={onOpen}
                  onUpdate={onUpdate}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const KanbanCard = ({ deal, budgets, dragging, onDragStart, onDragEnd, onOpen, onUpdate }) => {
  const { amount, estimated } = dealAmount(deal, budgets);
  const band = visibleUrgencyBand(deal);
  const overdue = isOverdue(deal.next_action_at);

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onOpen(deal)}
      className={`bg-white rounded-md border border-gray-200 p-2.5 shadow-sm cursor-grab hover:border-gray-300 hover:shadow ${dragging ? "opacity-50" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900 leading-tight">{deal.name}</p>
        <span className="text-xs font-semibold text-gray-700 whitespace-nowrap">{formatCurrency(amount)}</span>
      </div>

      <div className="mt-1.5 flex items-center gap-1 flex-wrap">
        <ActBadge deal={deal} />
        {band && (
          <span
            title={urgencySplit(deal)
              .filter((s) => s.p >= 0.05)
              .map((s) => `${Math.round(s.p * 100)}% ${s.label}`)
              .join("\n")}
            className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${band.color}`}>
            {band.label}
          </span>
        )}
        {isProbablyDead(deal) && (
          <span
            title="Jev thinks this opportunity has gone quiet. Worth closing out rather than chasing."
            className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-gray-200 text-gray-600">
            Dormant
          </span>
        )}
        {deal.blocker && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${blockerColor(deal.blocker)}`}>{blockerLabel(deal.blocker)}</span>
        )}
        {estimated && <span className="text-[10px] text-gray-400">estimate</span>}
      </div>

      {deal.next_action && <p className="mt-1.5 text-xs text-gray-600 line-clamp-2">{deal.next_action}</p>}

      <div className="mt-2 flex items-center justify-between gap-2">
        <Owners deal={deal} />
        {deal.next_action_at && <span className={`text-[11px] ${overdue ? "text-red-600 font-semibold" : "text-gray-500"}`}>{formatDate(deal.next_action_at)}</span>}
      </div>
      <div
        className="mt-1.5"
        draggable
        onClick={(e) => e.stopPropagation()}
        onDragStart={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}>
        <FollowUp deal={deal} onUpdate={onUpdate} />
      </div>
    </div>
  );
};

// Jev's direct answer to "does this need a person this week". Separate from the urgency pill
// because they answer different questions: urgency is where the deal sits on a rubric, this is
// whether to touch it. A deal can read "Routine" and still need a reply sent today.
const ActBadge = ({ deal }) => {
  if (!needsActionNow(deal)) return null;
  return (
    <span
      title={`Jev: ${Math.round(deal.sales_needs_action * 100)}% sure someone needs to act on this in the next few days`}
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase bg-red-600 text-white">
      Act
    </span>
  );
};

const Owners = ({ deal }) => {
  const people = [
    { name: deal.sales_owner_name, avatar: deal.sales_owner_avatar, role: "Sales owner" },
    { name: deal.closing_owner_name, avatar: deal.closing_owner_avatar, role: "Closing owner" },
  ].filter((p) => p.name);

  if (!people.length) return <span className="text-[11px] text-gray-400">Unassigned</span>;

  return (
    <div className="flex -space-x-1">
      {people.map((p) => (
        <img key={p.role} src={p.avatar} alt={p.name} title={`${p.role}: ${p.name}`} className="w-5 h-5 rounded-full border-2 border-white ring-1 ring-gray-200 object-cover" />
      ))}
    </div>
  );
};

// ─── Table ───────────────────────────────────────────────────────────────────
// One row per deal and read-only, on purpose: this view is for scanning and for management review.
// Every field is editable in the drawer, which opens on row click.

const DealTable = ({ deals, budgetsByProject, onOpen }) => (
  <div className="overflow-x-auto bg-white rounded-lg shadow">
    <table className="min-w-[1560px] w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr className="text-xs font-medium text-gray-500 uppercase tracking-wider">
          <th className="pl-4 pr-1 py-2 text-right w-10">#</th>
          <th className="px-3 py-2 text-left">Deal</th>
          <th className="px-3 py-2 text-left w-28">Stage</th>
          <th className="px-3 py-2 text-left w-36">Urgency</th>
          <th className="px-3 py-2 text-left w-40">Sales follow-up owner</th>
          <th className="px-3 py-2 text-left w-40">Closing / proposal owner</th>
          <th className="px-3 py-2 text-left w-40">Source / who brought the deal in</th>
          <th className="px-3 py-2 text-left w-40">Technical owner / lead dev</th>
          <th className="px-3 py-2 text-left w-28">Due</th>
          <th className="px-3 py-2 text-left w-36">Blocker</th>
          <th className="px-3 py-2 text-right w-32">Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {deals.map((deal, index) => (
          <DealRow key={deal._id} index={index} deal={deal} budgets={budgetsByProject[deal._id] || []} onOpen={onOpen} />
        ))}
      </tbody>
    </table>
  </div>
);

const DealRow = ({ index, deal, budgets, onOpen }) => {
  const { amount, estimated } = dealAmount(deal, budgets);
  const band = visibleUrgencyBand(deal);
  const reasons = actionReasons(deal);

  return (
    <tr onClick={() => onOpen(deal)} className="hover:bg-gray-50 cursor-pointer text-sm">
      <td className="pl-4 pr-1 py-2 text-right text-gray-400 tabular-nums">{index + 1}</td>
      <td className="px-3 py-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-semibold text-gray-900 truncate">{deal.name}</span>
          {deal.organisation_name && <span className="text-xs text-gray-400 whitespace-nowrap">{deal.organisation_name}</span>}
          {reasons.length > 0 && (
            <span title={reasons.join(" • ")} className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-red-100 text-red-700 whitespace-nowrap">
              {reasons.length === 1 ? reasons[0] : `${reasons.length} issues`}
            </span>
          )}
        </div>
      </td>

      <td className="px-3 py-2">
        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border whitespace-nowrap ${stageColor(deal.sales_stage)}`}>
          {stageLabel(deal.sales_stage)}
          {stageStep(deal.sales_stage) && <span className="ml-1.5 opacity-60 tabular-nums">{stageStep(deal.sales_stage)}</span>}
        </span>
      </td>

      <td className="px-3 py-2">
        <div className="flex items-center gap-1">
          <ActBadge deal={deal} />
          {band ? (
            <span
              title={urgencySplit(deal)
                .filter((s) => s.p >= 0.05)
                .map((s) => `${Math.round(s.p * 100)}% ${s.label}`)
                .join("\n")}
              className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase whitespace-nowrap ${band.color}`}>
              {band.label}
            </span>
          ) : (
            <span className="text-xs text-gray-300">—</span>
          )}
        </div>
      </td>

      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{deal.sales_owner_name || <span className="text-gray-300">—</span>}</td>
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{deal.closing_owner_name || <span className="text-gray-300">—</span>}</td>
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{deal.sales_business_provider_name || <span className="text-gray-300">—</span>}</td>
      <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{deal.lead_dev_name || <span className="text-gray-300">—</span>}</td>

      <td className={`px-3 py-2 whitespace-nowrap ${isOverdue(deal.next_action_at) ? "text-red-600 font-semibold" : "text-gray-500"}`}>
        {deal.next_action_at ? formatDate(deal.next_action_at) : <span className="text-gray-300">—</span>}
      </td>

      <td className="px-3 py-2">
        {deal.blocker ? (
          <span title={deal.blocker_description} className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${blockerColor(deal.blocker)}`}>
            {blockerLabel(deal.blocker)}
          </span>
        ) : (
          <span className="text-gray-300">—</span>
        )}
      </td>

      <td className="px-3 py-2 text-right font-semibold text-gray-900 whitespace-nowrap">
        {formatCurrency(amount)}
        {estimated && <span className="ml-1 text-[10px] font-normal text-gray-400">est.</span>}
      </td>
    </tr>
  );
};
